/* eslint-disable jsx-a11y/alt-text */
import _ from 'lodash';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { DatiVeicoloEntity, OptionalEntity } from '../../api';
import { useTranslation } from 'react-i18next';
import { isMarket } from '../../utils/markets';
import { Markets } from '../../types';
import { FORMAT_DATE } from '../../utils/constants';
import { fixSpaces, generatePdf } from '../../utils/pdfUtils';
import './VehicleDetails.scss';
import usePdfStore from 'src/store/PdfStore';
import { numberFormat } from 'src/utils';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { resolveReminderNumber } from 'src/utils/reminderNumber';
import { useQuotationRecorder } from 'src/quotations/useQuotationRecorder';

export type PDFData = {
	lista_offerte: {
		id: string;
		durata: string;
		km_totali: string;
		servizi: {
			treno_di_gomme: string,
			kasko: string,
			rca: string,
			auto_sostitutiva: string,
			i_f: string,
			manutenzione_ordinaria: boolean,
			manutenzione_straordinaria: boolean,
		};
		anticipo: string;
	}[];
	pdf: {
		data_validita: string;
		name: string;
		userName: string;
		userSurname: string;
		userMail: string;
		offerType: string;
		offerId: string;
		importo_azienda: string;
		importo_privato: string;
		dealerDetail: {
			logo: string;
		};
	};
	optionals: OptionalEntity[];
	dati_veicolo: DatiVeicoloEntity;
};

const VEHICLE_IMAGE_PLACEHOLDER = `${process.env.PUBLIC_URL}/images/placeholder.png`;

let oldPdfIdempotency = null;
function VehiclePdfTemplate(props: { onDone?: (filename: string, pdfBlob: Blob) => void }) {
	const maxlen = 500;
	const [data, setData] = useState<any>({});
	const [filename, setFilename] = useState<string | null>(null);
	const [offer, setoffer] = useState<any>({});
	const [specifications, setSpecifications] = useState<any[]>([]);

	const { t } = useTranslation();
	const quotationRecorder = useQuotationRecorder();

	const storeUnsubscribe = usePdfStore.subscribe((curr) => {
		if (curr.detail) {
			if (
				curr.detail.data.idempotency &&
				(!oldPdfIdempotency ||
					oldPdfIdempotency !== curr.detail.data.idempotency)
			) {
				oldPdfIdempotency = curr.detail.data.idempotency;
				downloadPdf(curr.detail);
			}
		}
	});

	useEffect(() => {
		if (
			Object.keys(data).length &&
			Object.keys(offer).length &&
			specifications.length &&
			filename
		) {
			generatePdf(
				(fileName, pdfBlob) => {
					quotationRecorder.record(data, offer);
					setData({});
					setoffer({});
					setFilename(null);
					setSpecifications([]);
					props.onDone?.(fileName, pdfBlob);
				},
				'#pdfContent',
				filename
			);
			// GTMsendEvent({
			// 	event: 'CTA_download_file',
			// 	// noleggiatore: values.processedRow.original.noleggiatore,
			// 	// veicolo: data.dati_veicolo,
			// 	// username: user.username,
			// 	// date: dayjs().format(FORMAT_DATE_TIME),
			// 	evtCat: 'preventivo_veicolo',
			// 	evtAct: "download",
			// 	evtLab: `${filename}`
			// });
		}
	}, [data, specifications, filename, offer])

	const getImageAsBase64 = (blob): Promise<string> => {
		return new Promise((resolve, rej) => {
			const fileReader = new FileReader();
			fileReader.onerror = () => rej();
			fileReader.onloadend = () => {
				resolve(fileReader.result as string);
			};

			fileReader.readAsDataURL(blob);
		});
	};

	const fetchImageAsBase64 = async (url: string): Promise<string | null> => {
		try {
			const response = await fetch(url);
			if (!response.ok) {
				return null;
			}

			return await getImageAsBase64(await response.blob());
		} catch (e) {
			console.warn('Could not inline image for PDF', url, e);
			return null;
		}
	};

	const downloadPdf = async (event: any) => {
		const { data, filename = `${t('quotation')}` } = event;
		quotationRecorder.ensureCode(data);
		data.totlen = data?.optionals?.reduce(
			(acc, optional) => acc + optional.descrizione.length,
			0
		);
		const offer =
			data?.lista_offerte?.find((offer) => offer.id === data?.pdf?.offerId) ||
			{};

		if (isMarket(Markets.es)) {
			const vehicleImage = data?.image_list?.[0];
			if (vehicleImage?.url && !vehicleImage.base64) {
				vehicleImage.base64 =
					(await fetchImageAsBase64(vehicleImage.url)) ??
					(await fetchImageAsBase64(VEHICLE_IMAGE_PLACEHOLDER));
			}
		}

		if (data?.pdf?.dealerDetail?.logo) {
			data.pdf.dealerDetail.logoDataUrl = await fetchImageAsBase64(
				data.pdf.dealerDetail.logo
			);
		}
		setData(data);
		setFilename(filename);
		setoffer(offer);
		setSpecifications([
			{
				label: i18next.t('fuel_type'),
				value: data.dati_veicolo?.alimentazione,
			},
			{
				label: i18next.t('kw'),
				value: data.dati_veicolo?.kw
					? `${data.dati_veicolo?.kw} kw`
					: undefined,
			},
			{
				label: i18next.t('cylinder_capacity'),
				value: data.dati_veicolo?.cilindrata,
			},
			{
				label: i18next.t('vehicle_transmission'),
				value: data.dati_veicolo?.cambio,
			},
			{
				label: i18next.t('vehicle_drive_system'),
				value: data.dati_veicolo?.trazione,
			},
			{ label: i18next.t('doors'), value: data.dati_veicolo?.porte },
			{ label: i18next.t('seats'), value: data.dati_veicolo?.posti },
		]);
	};

	useEffect(() => {
		return storeUnsubscribe;
	});

	const reminderNumber = useMemo(() => resolveReminderNumber(data), [data]);

	return (
		<div className="pdf-container">
			<div id="pdfContent" className="pdf">
				<table>
					<tbody>
						<tr>
							<td className="col-8">
								<img src="/images/pdf/horizon-c.png" className="logo" />
								<div className='space-block'></div>
								<p className="mb-1 big">
									{fixSpaces(`${t('dear')} ${data?.pdf?.name}`)}
								</p>
								<p className="big">
									{t('following_our_rental_offer_for_vehicle')}
								</p>

								<table>
									<tbody>
										<tr>
											<td className="col-6 car-preview">
												{!!data?.image_list?.length && (
													<>
														{data?.image_list[0].base64 ? (
															<img src={data?.image_list[0].base64} />
														) : (
															<img
																src={data?.image_list[0]?.url?.replace(
																	'https://api.horizonautomotive.it',
																	`${process.env.REACT_APP_API_URL}/images`
																)}
															/>
														)}
													</>
												)}
											</td>
											<td className="col-6 p-4">
												<div className="fw-bold">
													{data?.dati_veicolo?.marca}&nbsp;&nbsp;
													{data?.dati_veicolo?.modello.replaceAll(
														' ',
														'\xa0\xa0'
													)}
												</div>
												<div className="fw-bold">
													{data?.dati_veicolo?.allestimento.replaceAll(
														' ',
														'\xa0\xa0'
													)}
												</div>

												<p className="small m-0">
													{t('all_images_for_illustrative_purposes')}
												</p>
											</td>
										</tr>
									</tbody>
								</table>
								<hr className="light clear mx-5" />
								<table className="mt-3">
									<tbody>
										<tr>
											<td className="big-label">{t('rental_fee')}</td>
											<td className="big fw-bold">
												{offer[
													data?.pdf?.offerType === 'company'
														? 'importo_azienda'
														: 'importo_privato'
												]
													? numberFormat(offer[
														data?.pdf?.offerType === 'private'
															? 'importo_privato'
															: 'importo_azienda'
													]) + ' € / ' + t('month')
													: '-'}
											</td>
										</tr>
										<tr>
											<td className="big-label">{t('duration')}</td>
											<td className="big fw-bold">{numberFormat(offer?.durata)} {t('months')}</td>
										</tr>
										<tr>
											<td className="big-label">{t('km_included')}</td>
											<td className="big fw-bold">
												{numberFormat(offer?.km_totali)} / {t('year')}
											</td>
										</tr>
									</tbody>
								</table>
							</td>
							<td className="col-4">
								{data?.pdf?.dealerDetail?.logoDataUrl && (
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<img className='mb-3' src={data?.pdf?.dealerDetail?.logoDataUrl}
											style={{ objectFit: 'contain', maxWidth: 250, maxHeight: 120 }} alt="" />
									</div>
								)}
								<table
									className="info-user table-border mb-3 text-break"
									>
									<tbody>
										<tr>
											<td className="col-12">
												<div className="p-2 pt-0">
													{t('author')}
													<div className="fw-bold text-spacing">{data?.pdf?.userName + ' ' + data?.pdf?.userSurname}</div>
												</div>
											</td>
										</tr>
										<tr>
											<td className="col-12">
												<div className="p-2 pt-0">
													{t('mail')}
													<div className="fw-bold text-spacing">{data?.pdf?.userMail}</div>
												</div>
											</td>
										</tr>
										{data?.pdf?.userPhone ? (
											<tr>
												<td className="col-12">
													<div className="p-2 pt-0">
														{t('phone')}
														<div className="fw-bold text-spacing">{data?.pdf?.userPhone}</div>
													</div>
												</td>
											</tr>
										) : ''}
									</tbody>
								</table>

								{/*  promemoria numero */}
								<div className="mb-3">
									{t('reminder_number')}
									<div className="fw-bold">{reminderNumber}</div>
								</div>

								{/* Intestatario */}
								<div className="mb-3">
									{t('recipient')}
									<div className="fw-bold">
										{data?.pdf?.name?.replaceAll(' ', '\xa0\xa0')}
									</div>
								</div>

								{/* Data validità */}
								<div>
									{t('validity_date')}
									<div className="fw-bold">
										{data?.pdf?.data_validita || dayjs().add(3, 'weeks').format(FORMAT_DATE)}
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<hr className="big" />

				<table>
					<tbody>
						<tr>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/chracteristics-${process.env.REACT_APP_MARKET}.png`} className="float-start pdf-icon"
								/>
							</td>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/included_services-${process.env.REACT_APP_MARKET}.png`}
									className="float-start pdf-icon"
								/>
							</td>
							<td className="col-4 p-2">
								<img
									src={`/images/pdf/offer-${process.env.REACT_APP_MARKET}.png`}
									className="float-start pdf-icon"
								/>
							</td>
						</tr>
					</tbody>
				</table>

				<hr className="big" />

				<table className="info">
					<tbody>
						<tr>
							<td className="col-4 col-border">
								<div className="p-2 pt-0  ">
									{(data ? [
										{ label: i18next.t('fuel_type'), value: data.dati_veicolo?.alimentazione },
										{ label: i18next.t('kw'), value: data.dati_veicolo?.kw ? `${data.dati_veicolo?.kw} kw` : undefined },
										{ label: i18next.t('cylinder_capacity'), value: data.dati_veicolo?.cilindrata },
										{ label: i18next.t('vehicle_transmission'), value: data.dati_veicolo?.cambio },
										{ label: i18next.t('vehicle_drive_system'), value: data.dati_veicolo?.trazione },
										{ label: i18next.t('doors'), value: data.dati_veicolo?.porte },
										{ label: i18next.t('seats'), value: data.dati_veicolo?.posti },
									] : []).map((spec, index) => {
										return spec.value ? (
											<div key={index}>
												{spec.label}: {fixSpaces(spec.value)}
											</div>
										) : null
									})}
								</div>

								<div className="gray-box fw-bold pe-0 w-100">
									{fixSpaces(t('accessories_and_additional_optionals'))}
									&nbsp;&nbsp; ({data?.optionals?.length})
								</div>
								<div className="p-2 pt-0">

									{data.totlen < maxlen ?
										<ul>
											{data?.optionals?.map((optional, index: number) => (
												<li key={index}>
													{_.upperFirst(optional.descrizione.toLowerCase())}: {optional.valore ? fixSpaces(optional.valore.toLocaleString() + ' €') : ''}
												</li>
											))}
										</ul>
										:
										<span>{t('view_optionals_on_following_page')}</span>
									}

								</div>
							</td>

							{/* SERVIZI SERVICES */}
							<td className="col-4 col-border">
								<div className="p-2 pt-0">
									<ul>
										{data?.pdf?.services
											?.split(/[\n]+/)
											.map((service: string, index: number) => {
												service = service.trim()
												if (service.length) {
													return <li key={index}>{service}</li>
												}
												return null
											})}

										{/* todo visto che è stato fatto in fretta e furia andrà refattorizzato */}
										{isMarket(Markets.es) && (
											<>

												<>
													{data?.pdf?.assistenza_stradale && (
														<li>{i18next.t("roadside_assistance")}: sì</li>
													)}
													{data?.pdf?.manutenzione_straordinaria && (
														<li>{i18next.t("straordinary_maintenance")}: sì</li>
													)}
													{data?.pdf?.manutenzione_ordinaria && (
														<li>{i18next.t("ordinary_maintenance")}: sì</li>
													)}
													{data?.pdf?.costo_eccesso && (
														<li>
															{i18next.t("excess_costs_km")}: {data?.pdf?.costo_eccesso_km}
														</li>
													)}
													{data?.pdf?.is_Prezzo_Km_Non_Usati && (
														<li>
															{i18next.t("price_unused_km")}: {data?.pdf?.prezzo_km_non_usati}
														</li>
													)}
												</>
											</>
										)}
									</ul>
								</div>
							</td>
							<td className="col-4">
								<div className="p-2  pt-0">
									<div className="float-start w-100 mb-2">

										{/* Canone totale iva esclusa */}
										<div className="float-start fw-bold">
											{i18next.t('total_rental_fee').replaceAll(' ', '\xa0\xa0')} <br />
											{(
												(data?.pdf?.offerType === 'company' ? i18next.t('vat_excluded') : i18next.t('vat_included'))
											).replaceAll(' ', '\xa0\xa0')}
										</div>

										<div className="float-end fw-bold">
											{offer[
												data?.pdf?.offerType === 'company'
													? 'importo_azienda'
													: 'importo_privato'
											]
												? numberFormat(offer[
													data?.pdf?.offerType === 'private'
														? 'importo_privato'
														: 'importo_azienda'
												]) + ' € *'
												: '-'}
										</div>
									</div>
									<hr className="light clear" />
									<div className="mt-2 float-start w-100 fw-bold">
										<div className="float-start big-label">{t('downpayment')}</div>
										<div className="float-end big mb-1">
											{numberFormat(offer.anticipo) || 0} € **
										</div>
									</div>
									<hr className="light clear" />
									* {t('quoted_rate_excludes_additional_accessories')}
									<br />
									<br />
									** {t('all_downpayments_exempt_from_vat')}
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<table className="mt-1" style={{}}>
					<tbody>
						<tr>
							<td className="footer">
								<img src="/images/pdf/logo-footer.png" className="float-end" />
								<br />
								{t('offer_valid_15_business_days')}
							</td>
						</tr>
					</tbody>
				</table>

				{data.totlen >= maxlen ?
					<>
						<table className='optionals'>
							<tbody>
								<tr>
									<td className="pt-3">
										<img src="/images/pdf/horizon-c.png" className="logo mb-0" />
									</td>
								</tr>
								<tr>
									<td className="">
										<span className="fw-bold big">
											{i18next.t('accessories_and_additional_optionals').replaceAll(
												' ',
												'\xa0\xa0'
											)}
											&nbsp;&nbsp; ({data?.optionals?.length})
										</span>
										<ul>
											{data?.optionals?.map((optional, index: number) => (
												<li key={index}>
													{_.upperFirst(optional.descrizione.toLowerCase())}
												</li>
											))}
										</ul>
									</td>
								</tr>
							</tbody>
						</table>

						<table className=''>
							<tbody>
								<tr>
									<td className="footer">
										<img src="/images/pdf/logo-footer.png" className="float-end" />
										<br />
										{t('offer_valid_15_business_days')}
									</td>
								</tr>
							</tbody>
						</table>
					</>
					: ""
				}

			</div>
		</div >
	);
}

export default VehiclePdfTemplate;
